import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../context/TitleContext';
import { loadTemplates, saveTemplates, DocumentTemplate, DocumentTemplateItem, generateId } from '../models/templateModels';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Plus, X, GripVertical } from 'lucide-react';

const TemplateEditorNew = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const { t } = useTranslation();
  const { setTitle } = useTitle();
  const { currentUser } = useAuth();
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [items, setItems] = useState<DocumentTemplateItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    setTitle(templateId === 'new' ? t('createTemplate') : t('editTemplate'));
    document.title = templateId === 'new' ? t('createTemplate') : t('editTemplate');
  }, [setTitle, t, templateId]);

  useEffect(() => {
    if (templateId && templateId !== 'new') {
      const templates = loadTemplates();
      const foundTemplate = templates.find(t => t.id === templateId);
      if (foundTemplate) {
        setTemplateName(foundTemplate.name);
        setTemplateDescription(foundTemplate.description || '');
        setItems(foundTemplate.items || []);
      }
    }
  }, [templateId]);

  const handleAddItem = () => {
    const newItem: DocumentTemplateItem = {
      id: generateId(),
      nameKey: '',
      customName: '',
      descriptionKey: '',
      customDescription: '',
      isRequired: true,
      order: items.length + 1
    };
    setItems([...items, newItem]);
  };

  const handleRemoveItem = (itemId: string) => {
    setItems(items.filter(item => item.id !== itemId));
  };

  const handleItemChange = (itemId: string, field: keyof DocumentTemplateItem, value: any) => {
    setItems(items.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, draggedItem);
    
    setItems(newItems);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSaveAndContinue = () => {
    // Validation
    if (!templateName.trim()) {
      alert(t('templateNameRequired'));
      return;
    }

    if (items.length === 0) {
      alert(t('atLeastOneDocumentRequired'));
      return;
    }

    const hasEmptyName = items.some(item => !item.customName?.trim());
    if (hasEmptyName) {
      alert(t('allDocumentsMustHaveName'));
      return;
    }

    setIsSaving(true);

    try {
      console.log('Saving template, templateId:', templateId);
      const templates = loadTemplates();
      const now = new Date().toISOString();

      // Check if creating new or editing existing
      if (!templateId || templateId === 'new') {
        // Create new template
        const newTemplate: DocumentTemplate = {
          id: generateId(),
          name: templateName,
          description: templateDescription,
          items: items,
          createdAt: now,
          updatedAt: now,
          createdBy: currentUser?.id || 'unknown'
        };
        templates.push(newTemplate);
        saveTemplates(templates);
        
        // Immediate redirect - use setTimeout to ensure localStorage save completes
        console.log('Template saved successfully, redirecting to My Templates...');
        setTimeout(() => {
          window.location.href = '/my-templates';
        }, 100);
      } else {
        // Update existing template
        const index = templates.findIndex(t => t.id === templateId);
        if (index !== -1) {
          templates[index] = {
            ...templates[index],
            name: templateName,
            description: templateDescription,
            items: items,
            updatedAt: now
          };
          saveTemplates(templates);
          
          // Redirect to My Templates instead of send page
          console.log('Template updated successfully, redirecting to My Templates...');
          setTimeout(() => {
            window.location.href = '/my-templates';
          }, 100);
        } else {
          console.error('Template not found with id:', templateId);
          throw new Error('Template not found');
        }
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template. Please try again.');
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link to="/my-templates" className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToTemplates')}
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {templateId === 'new' ? t('createNewTemplate') : t('editTemplate')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('templateEditorDescription')}
          </p>
        </div>

        {/* Template Info */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('templateInformation')}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('templateName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder={t('enterTemplateName')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('description')} ({t('optional')})
              </label>
              <textarea
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder={t('enterTemplateDescription')}
              />
            </div>
          </div>
        </div>

        {/* Document Items */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('documents')}
            </h2>
            <button
              onClick={handleAddItem}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('addDocument')}
            </button>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {t('noDocumentsYet')}
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item, index) => (
                <div 
                  key={item.id} 
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-move ${
                    draggedIndex === index ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-5 w-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('document')} #{index + 1}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      className="text-red-600 hover:text-red-700 dark:text-red-400"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('documentName')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={item.customName || ''}
                        onChange={(e) => handleItemChange(item.id, 'customName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder={t('enterDocumentName')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('description')} ({t('optional')})
                      </label>
                      <input
                        type="text"
                        value={item.customDescription || ''}
                        onChange={(e) => handleItemChange(item.id, 'customDescription', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder={t('enterDocumentDescription')}
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`required-${item.id}`}
                        checked={item.isRequired}
                        onChange={(e) => handleItemChange(item.id, 'isRequired', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`required-${item.id}`} className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                        {t('requiredDocument')}
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <Link
            to="/my-templates"
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-center"
          >
            {t('cancel')}
          </Link>
          <button
            onClick={handleSaveAndContinue}
            disabled={isSaving}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? t('saving') : t('saveAndContinue')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditorNew;
